export type Locale =
  | "en"
  | "hi"
  | "es"
  | "fr"
  | "de"
  | "pt"
  | "it"
  | "nl"
  | "ru"
  | "ja"
  | "ko"
  | "zh"
  | "ar"
  | "tr"
  | "pl"
  | "sv"
  | "no"
  | "da"
  | "fi"
  | "el"
  | "cs"
  | "sk"
  | "hu"
  | "ro"
  | "uk"
  | "id"
  | "vi"
  | "th"
  | "ms"
  | "he"
  | "bn"
  | "ta"
  | "mr"
  | "gu";

export const localeOptions = [
  { code: "en" as const, label: "English", nativeLabel: "English", dateLocale: "en-IN" },
  { code: "hi" as const, label: "Hindi", nativeLabel: "हिंदी", dateLocale: "hi-IN" },
  { code: "es" as const, label: "Spanish", nativeLabel: "Español", dateLocale: "es-ES" },
  { code: "fr" as const, label: "French", nativeLabel: "Français", dateLocale: "fr-FR" },
  { code: "de" as const, label: "German", nativeLabel: "Deutsch", dateLocale: "de-DE" },
  { code: "pt" as const, label: "Portuguese", nativeLabel: "Português", dateLocale: "pt-PT" },
  { code: "it" as const, label: "Italian", nativeLabel: "Italiano", dateLocale: "it-IT" },
  { code: "nl" as const, label: "Dutch", nativeLabel: "Nederlands", dateLocale: "nl-NL" },
  { code: "ru" as const, label: "Russian", nativeLabel: "Русский", dateLocale: "ru-RU" },
  { code: "ja" as const, label: "Japanese", nativeLabel: "日本語", dateLocale: "ja-JP" },
  { code: "ko" as const, label: "Korean", nativeLabel: "한국어", dateLocale: "ko-KR" },
  { code: "zh" as const, label: "Chinese", nativeLabel: "中文", dateLocale: "zh-CN" },
  { code: "ar" as const, label: "Arabic", nativeLabel: "العربية", dateLocale: "ar-EG" },
  { code: "tr" as const, label: "Turkish", nativeLabel: "Türkçe", dateLocale: "tr-TR" },
  { code: "pl" as const, label: "Polish", nativeLabel: "Polski", dateLocale: "pl-PL" },
  { code: "sv" as const, label: "Swedish", nativeLabel: "Svenska", dateLocale: "sv-SE" },
  { code: "no" as const, label: "Norwegian", nativeLabel: "Norsk", dateLocale: "nb-NO" },
  { code: "da" as const, label: "Danish", nativeLabel: "Dansk", dateLocale: "da-DK" },
  { code: "fi" as const, label: "Finnish", nativeLabel: "Suomi", dateLocale: "fi-FI" },
  { code: "el" as const, label: "Greek", nativeLabel: "Ελληνικά", dateLocale: "el-GR" },
  { code: "cs" as const, label: "Czech", nativeLabel: "Čeština", dateLocale: "cs-CZ" },
  { code: "sk" as const, label: "Slovak", nativeLabel: "Slovenčina", dateLocale: "sk-SK" },
  { code: "hu" as const, label: "Hungarian", nativeLabel: "Magyar", dateLocale: "hu-HU" },
  { code: "ro" as const, label: "Romanian", nativeLabel: "Română", dateLocale: "ro-RO" },
  { code: "uk" as const, label: "Ukrainian", nativeLabel: "Українська", dateLocale: "uk-UA" },
  { code: "id" as const, label: "Indonesian", nativeLabel: "Bahasa Indonesia", dateLocale: "id-ID" },
  { code: "vi" as const, label: "Vietnamese", nativeLabel: "Tiếng Việt", dateLocale: "vi-VN" },
  { code: "th" as const, label: "Thai", nativeLabel: "ไทย", dateLocale: "th-TH" },
  { code: "ms" as const, label: "Malay", nativeLabel: "Bahasa Melayu", dateLocale: "ms-MY" },
  { code: "he" as const, label: "Hebrew", nativeLabel: "עברית", dateLocale: "he-IL" },
  { code: "bn" as const, label: "Bengali", nativeLabel: "বাংলা", dateLocale: "bn-BD" },
  { code: "ta" as const, label: "Tamil", nativeLabel: "தமிழ்", dateLocale: "ta-IN" },
  { code: "mr" as const, label: "Marathi", nativeLabel: "मराठी", dateLocale: "mr-IN" },
  { code: "gu" as const, label: "Gujarati", nativeLabel: "ગુજરાતી", dateLocale: "gu-IN" },
] as const;

export type SeoCopy = {
  title: string;
  description: string;
  keywords: string[];
  faq: { question: string; answer: string }[];
};

export type Copy = {
  seo: SeoCopy;
  headerTagline: string;
  headerTitle: string;
  reset: string;
  switchToLight: string;
  switchToDark: string;
  languageLabel: string;
  languageEnglish: string;
  languageHindi: string;
  heroEyebrow: string;
  heroTitle: string;
  heroDescription: string;
  yearSelectLabel: string;
  monthSelectLabel: string;
  includeMonthLabel: string;
  includeMonthNote: string;
  yearOnlyBadge: string;
  yearMonthBadge: string;
  yearPlaceholder: string;
  monthPlaceholder: string;
  inputTitle: string;
  inputLabel: string;
  blockedDateNote: string;
  result: string;
  vehicleAgeLead: string;
  asOfToday: string;
  years: string;
  months: string;
  days: string;
  totalDays: (count: number) => string;
  ageLabels: {
    almostNew: string;
    modern: string;
    mature: string;
    classic: string;
  };
  estimatedValue: {
    high: string;
    healthy: string;
    moderate: string;
    collector: string;
  };
  maintenanceTitle: string;
  maintenance: {
    routine: string;
    watch: string;
    plan: string;
    specialized: string;
  };
  registrationTitle: string;
  registration: {
    collector: string;
    classic: string;
    standard: string;
    regular: string;
  };
  upcomingMilestoneTitle: string;
  milestoneTimelineTitle: string;
  milestoneRemaining: (args: { label: string; days: number }) => string;
  reached: string;
  upcoming: string;
  allMilestonesReachedTitle: string;
  allMilestonesReachedBody: string;
  copySummary: string;
  shareResult: string;
  resetMessage: string;
  summaryCopied: string;
  clipboardFailed: string;
  emptyTitle: string;
  emptyDescription: string;
  shareTitle: string;
  summary: (args: { label: string; years: number; months: number; days: number; asOf: string }) => string;
  milestones: Record<number, { label: string; note: string }>;
};
